import { shouldAnimate, transition } from '@helpers/animate';
import { imageCoverContainer } from '@helpers/aspectRatio';
import brand from '@helpers/brand';
import { fluid } from '@helpers/fluid';
import fonts from '@helpers/fonts';
import { ButtonReset } from '@helpers/global';
import { Device, from, until } from '@helpers/media';
import srOnly from '@helpers/srOnly';
import HeadingSummaryBlockStyles from '@stories/Components/Misc/HeadingSummaryBlock/HeadingSummaryBlock.styles';
import SectionWrapper from '@stories/Components/Misc/SectionWrapper/SectionWrapper';
import SectionWrapperStyles from '@stories/Components/Misc/SectionWrapper/SectionWrapper.styles';
import { rgba } from 'polished';
import styled, { css } from 'styled-components';
const Container = styled(SectionWrapper) `
  --headingColour: ${brand.primary.purple.light};
  --highlightColour: ${brand.primary.purple.light};
  --highlightTextColour: ${brand.global.white};
  --sectionBackground: ${brand.global.white};

  overflow: hidden;

  ${SectionWrapperStyles.Inner} {
    display: grid;
  }

  ${HeadingSummaryBlockStyles.Container} {
    color: ${brand.primary.blue.dark};
    margin-bottom: ${fluid(40, 80)};
  }

  @media print {
    display: none;
  }
`;
const TabWrapper = styled.div `
  --buttonHeight: ${fluid(56, 88)};

  position: relative;
  margin: calc(${fluid(20, 40)} * -1) 0 ${fluid(40, 80)};

  &[data-hidden='true'] {
    ${srOnly};
  }

  &:has(button:nth-of-type(3)) {
    height: var(--buttonHeight);
  }

  &::before {
    ${transition('max-width', 0.65)};

    content: '';
    position: absolute;
    top: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 100dvw;
    height: 100%;
    background-color: var(--sectionBackground);
    z-index: 1;
    box-shadow: 0px 0px 20px 0px rgba(0, 0, 0, 0.1);
    max-width: 100dvw;

    ${Container}[data-animate='false'] & {
      max-width: 0px;
    }
  }
`;
const TabButton = styled.button `
  ${ButtonReset};
  ${transition('color, opacity, order, max-height, transform')};
  ${fonts.lato.bold};

  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${fluid(6, 10)};
  z-index: 1;
  padding: 0 ${fluid(28, 72)};
  color: ${brand.grey.dark};
  height: var(--buttonHeight);
  max-height: var(--buttonHeight);
  text-align: center;
  font-size: ${fluid(16, 20)};
  line-height: ${fluid(24, 30)};

  &:focus-visible {
    outline: auto;
    outline-offset: 3px;
    outline-width: 2px;
  }

  &:hover,
  &[aria-selected='true'] {
    color: ${brand.primary.purple.light};
  }

  &::before {
    ${transition('max-width', 0.25)};

    content: '';
    position: absolute;
    top: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 100%;
    height: 2px;
    background-color: ${brand.primary.purple.light};
    max-width: 100%;
  }

  &[aria-selected='false'],
  ${Container}[data-animate='false'] & {
    &::before {
      max-width: 0px;
    }
  }

  ${Container}[data-animate='false'] & {
    opacity: 0;
  }

  ${Container}[data-delays='true'] & {
    transition-delay: 250ms;

    &::before {
      transition-delay: 650ms;
    }
  }

  svg {
    ${transition('transform', 0.25)};

    display: none;
    width: ${fluid(20, 26)};
  }
`;
const Divider = styled.div `
  ${transition('max-height')};

  width: 2px;
  height: var(--buttonHeight);
  max-height: var(--buttonHeight);
  background-color: ${brand.grey.lightest};
  z-index: 1;
  transition-delay: 400ms;

  ${Container}[data-animate='false'] & {
    max-height: 0px;
  }
`;
const Tabs = styled.div `
  display: flex;
  justify-content: center;

  @media ${until(Device.MobileLarge)} {
    &:not(:has(button:nth-of-type(3))) {
      margin: 0 calc(var(--sitePadding) * -1);

      ${TabButton} {
        flex: 1;
      }
    }
  }

  /* Buttons display as dropdown list if more than 2 */
  &:has(button:nth-of-type(3)) {
    flex-direction: column;
    background: var(--sectionBackground);
    margin: 0 auto;
    width: min-content;

    ${TabButton} {
      white-space: nowrap;
    }

    ${TabButton}[aria-selected='true'] {
      order: 0;

      svg {
        display: block;

        ${TabWrapper}[data-open='true'] & {
          transform: rotate(180deg);
        }
      }
    }

    ${TabButton}:not([aria-selected='true']) {
      max-height: calc(var(--buttonHeight) * 0.66);
      background-color: var(--sectionBackground);
      opacity: 0;
      pointer-events: none;
      order: 1;
      box-shadow: 0px 2px 3px 0px rgba(0, 0, 0, 0.1);
      transform: translateY(-10px);

      ${TabWrapper}[data-open= 'true'] & {
        opacity: 1;
        pointer-events: auto;
        transform: none;
      }
    }
  }
`;
const LogosWrapper = styled.div `
  ${transition('opacity, transform, visibility', 0.5)};

  position: relative;
  grid-column: 1;
  grid-row: 3;
  overflow: hidden;

  &::before,
  &::after {
    position: absolute;
    top: 0;
    content: '';
    display: block;
    height: 100%;
    background-image: linear-gradient(
      90deg,
      ${brand.global.white} 10%,
      ${rgba(brand.global.white, 0.33)} 60%,
      transparent
    );
    width: ${fluid(32, 48)};
    z-index: 2;
  }

  &::before {
    left: 0;
  }

  &::after {
    right: 0;
    transform: scaleX(-1);
  }

  ${Container}[data-animate='false'] &,
  &[data-active='false'] {
    opacity: 0;
    pointer-events: none;
    visibility: hidden;
  }

  ${Container}[data-animate='false'] & {
    transform: translateY(20px);
  }

  ${Container}[data-delays='true'] & {
    transition-delay: 750ms;
  }

  @media ${until(Device.TabletLarge)} {
    margin: 0 calc(var(--sitePadding) * -1);
  }
`;
const LogosTrack = styled.div `
  --speed: 7s;
  --count: 5;
  --cardGap: ${fluid(24, 64)};
  --cardWidth: ${fluid(108, 206)};

  display: flex;
  gap: var(--cardGap);

  ${shouldAnimate(css `
    animation: scrolling var(--speed) linear infinite;
  `)}

  ${Container}[data-animate='false'] &,
  ${LogosWrapper}[data-active='false'] &,
  &:hover {
    animation-play-state: paused;
  }

  @keyframes scrolling {
    0% {
      transform: translateX(0);
    }

    100% {
      transform: translateX(calc(var(--count) * (var(--cardGap) + var(--cardWidth)) * -1));
    }
  }
`;
const Card = styled.div `
  ${fonts.lato.bold};

  display: flex;
  gap: ${fluid(16, 34)};
  flex-direction: column;
  flex: 0 0 var(--cardWidth);
  align-items: center;
  font-size: ${fluid(14, 16)};
  line-height: ${fluid(24, 26)};
  text-align: center;
`;
const ImageWrapper = styled.div `
  position: relative;
  height: ${fluid(32, 64)};
  width: 100%;
`;
const Image = styled.img `
  ${imageCoverContainer()};

  object-fit: contain;
`;
const ButtonWrapper = styled.div `
  ${transition('opacity, transform')};

  display: flex;
  gap: 16px;
  flex-direction: column;
  align-items: center;
  margin: ${fluid(40, 80)} 0 0;
  grid-column: 1;
  grid-row: 4;
  padding-bottom: 1px;

  ${Container}[data-animate='false'] & {
    opacity: 0;
    transform: translateY(20px);
  }

  ${Container}[data-delays='true'] & {
    transition-delay: 900ms;
  }

  @media ${from(Device.TabletLarge)} {
    flex-direction: row;
    justify-content: center;
  }
`;
const LogoListStyles = {
    Container,
    Tabs,
    LogosWrapper,
    Card,
    Image,
    ImageWrapper,
    LogosTrack,
    TabButton,
    TabWrapper,
    Divider,
    ButtonWrapper,
};
export default LogoListStyles;
